import { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { MessageCircle, Send, X, Loader2, User, ThumbsUp, ThumbsDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session expiry: 30 days
const SESSION_EXPIRY_DAYS = 30;

export default function ChatWindow({ actorId, actorName, actorImage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState("initial"); // initial, chat
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [remainingMessages, setRemainingMessages] = useState(10);
  const [feedbackGiven, setFeedbackGiven] = useState({}); // Track feedback per message
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, [actorId]);

  // Auto-focus input when chat is ready and after sending message
  useEffect(() => {
    if (step === "chat" && !sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [step, sending, messages]);

  const checkExistingSession = async () => {
    try {
      const storageKey = `chat_session_${actorId}`;
      const savedSession = localStorage.getItem(storageKey);
      
      if (savedSession) {
        const { sessionId, name, phone, expiryDate } = JSON.parse(savedSession);
        
        // Check if session is still valid (within 30 days)
        const now = new Date().getTime();
        const expiry = new Date(expiryDate).getTime();
        
        if (now < expiry) {
          // Session is still valid, restore it
          setSessionId(sessionId);
          setName(name);
          setPhone(phone || "");
          setStep("chat");
          
          // Add welcome back message
          setMessages([
            {
              role: "assistant",
              content: `Welcome back, ${name}! I'm ${actorName}. How can I help you today?`,
              timestamp: new Date().toISOString()
            }
          ]);
        } else {
          // Session expired, clear it
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.error("Error checking existing session:", error);
    }
  };

  const startChat = async () => {
    if (!name || !phone) {
      toast.error("Please enter your name and phone number");
      return;
    }

    setLoading(true);
    try {
      // Call API to initialize chat session
      const response = await axios.post(`${API}/public/chat/message`, {
        session_id: null,
        actor_id: actorId,
        visitor_name: name,
        visitor_phone: phone,
        message: "" // Empty message to initialize session
      });
      
      // Get session_id from response
      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      
      // Save session to localStorage with 30-day expiry
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + SESSION_EXPIRY_DAYS);
      
      const storageKey = `chat_session_${actorId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        sessionId: newSessionId,
        name: name,
        phone: phone,
        expiryDate: expiryDate.toISOString()
      }));
      
      setStep("chat");
      
      // Add welcome message if provided in response, otherwise use default
      const welcomeMessage = response.data.message || `Hi ${name}! I'm ${actorName}. How can I help you today?`;
      setMessages([
        {
          role: "assistant",
          content: welcomeMessage,
          timestamp: new Date().toISOString()
        }
      ]);
      
      // Update remaining messages if provided
      if (response.data.remaining_messages !== undefined) {
        setRemainingMessages(response.data.remaining_messages);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start chat");
      console.error("Error starting chat:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage = newMessage.trim();
    setNewMessage("");
    setSending(true);

    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    let isConnectionLost = false;

    try {
      const response = await axios.post(`${API}/public/chat/message`, {
        session_id: sessionId || "",
        message: userMessage,
        actor_id: actorId,
        visitor_name: name,
        visitor_phone: phone
      });
      
      // Update session ID if backend created a new one
      if (response.data.session_id && response.data.session_id !== sessionId) {
        setSessionId(response.data.session_id);
        
        // Update localStorage with new session ID
        const storageKey = `chat_session_${actorId}`;
        const savedSession = localStorage.getItem(storageKey);
        if (savedSession) {
          const sessionData = JSON.parse(savedSession);
          sessionData.sessionId = response.data.session_id;
          localStorage.setItem(storageKey, JSON.stringify(sessionData));
        }
      }

      // Add assistant response with message ID for feedback
      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.message,
        message_id: response.data.message_id, // Store message ID for feedback
        timestamp: new Date().toISOString()
      }]);

      setRemainingMessages(response.data.remaining_messages);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || "Failed to send message";
      
      // Check if it's a connection lost error
      if (errorDetail === "CONNECTION_LOST" || error.response?.status === 503) {
        isConnectionLost = true;
        toast.error("Connection lost. Please try again later.");
        // Disable chat
        setMessages(prev => [...prev, {
          role: "system",
          content: "⚠️ Connection lost. The chat has been disconnected. Please refresh the page to start a new conversation.",
          timestamp: new Date().toISOString()
        }]);
        // Keep it disabled by NOT setting setSending(false)
      } else {
        toast.error(errorDetail);
        // Remove the optimistic user message on error
        setMessages(prev => prev.slice(0, -1));
        setNewMessage(userMessage); // Restore the message
      }
    } finally {
      // Only re-enable sending if it wasn't a connection lost error
      if (!isConnectionLost) {
        setSending(false);
      }
      
      // Keep focus on input after sending
      if (inputRef.current && !isConnectionLost) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  };

  const submitFeedback = async (messageId, feedbackType) => {
    try {
      await axios.post(`${API}/public/chat/feedback`, {
        session_id: sessionId,
        actor_id: actorId,
        message_id: messageId,
        feedback_type: feedbackType,
        feedback_comment: null
      });
      
      setFeedbackGiven(prev => ({ ...prev, [messageId]: feedbackType }));
      toast.success(`Thank you for your feedback!`);
    } catch (error) {
      toast.error("Failed to submit feedback");
      console.error(error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (step === "initial") {
        startChat();
      } else {
        sendMessage();
      }
    }
  };

  return (
    <>
      {/* Floating Chat Button with Actor Image */}
      {!isOpen && (
        <div className="fixed bottom-24 right-6 z-[9998]">
          <button
            onClick={() => setIsOpen(true)}
            className="relative group"
          >
            {/* Actor Profile Image */}
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-2xl hover:scale-110 transition-transform duration-200 bg-slate-900">
              {actorImage ? (
                <img
                  src={`${API}/ai/actors/images/${actorImage}`}
                  alt={actorName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-900">
                  <User className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
            
            {/* Pulsing indicator */}
            <div className="absolute -top-1 -right-1">
              <div className="relative">
                <div className="w-5 h-5 bg-emerald-500 rounded-full border-2 border-white"></div>
                <div className="absolute inset-0 w-5 h-5 bg-emerald-500 rounded-full animate-ping"></div>
              </div>
            </div>
            
            {/* Chat message icon */}
            <div className="absolute -bottom-2 -right-2 bg-slate-900 rounded-full p-2 shadow-lg">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Chat with {actorName}
              <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
            </div>
          </button>
        </div>
      )}

      {/* Chat Window - Higher z-index to be above "Made with Emergent" badge */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-[10000] w-[calc(100vw-3rem)] h-[calc(100vh-8rem)] md:bottom-0 md:right-0 md:top-0 md:w-[50vw] md:h-screen max-w-full">
          <Card className="shadow-2xl border-2 h-full flex flex-col">
            <CardHeader className="bg-slate-900 text-white rounded-t-lg md:rounded-none flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Chat with {actorName}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-slate-800"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-0 flex-1 flex flex-col overflow-hidden">
              {step === "initial" && (
                <div className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter your details to start chatting.
                  </p>
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Your phone number"
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <Button
                    onClick={startChat}
                    disabled={loading || !name || !phone}
                    className="w-full bg-slate-900 hover:bg-slate-800"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start Chat"}
                  </Button>
                </div>
              )}

              {step === "chat" && (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                    {messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            msg.role === "user"
                              ? "bg-slate-900 text-white"
                              : "bg-white border border-slate-200"
                          }`}
                        >
                          <div className="text-sm prose prose-sm max-w-none">
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          </div>
                        </div>
                        {/* Feedback Buttons for Assistant Messages */}
                        {msg.role === "assistant" && msg.message_id && (
                          <div className="flex items-center gap-2 mt-2 ml-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => submitFeedback(msg.message_id, "positive")}
                              disabled={feedbackGiven[msg.message_id]}
                              className={`h-7 px-2 ${
                                feedbackGiven[msg.message_id] === "positive"
                                  ? "text-green-600 bg-green-50"
                                  : "text-slate-400 hover:text-green-600 hover:bg-green-50"
                              }`}
                            >
                              <ThumbsUp className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => submitFeedback(msg.message_id, "negative")}
                              disabled={feedbackGiven[msg.message_id]}
                              className={`h-7 px-2 ${
                                feedbackGiven[msg.message_id] === "negative"
                                  ? "text-red-600 bg-red-50"
                                  : "text-slate-400 hover:text-red-600 hover:bg-red-50"
                              }`}
                            >
                              <ThumbsDown className="w-3 h-3" />
                            </Button>
                            {feedbackGiven[msg.message_id] && (
                              <span className="text-xs text-muted-foreground">Thank you!</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {sending && (
                      <div className="flex justify-start">
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="p-4 border-t flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <Input
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        onKeyPress={handleKeyPress}
                        disabled={sending || remainingMessages <= 0}
                        autoFocus
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={sending || !newMessage.trim() || remainingMessages <= 0}
                        className="bg-slate-900 hover:bg-slate-800"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {remainingMessages} messages remaining today
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
